/* @jsx h */
import { defineComponent, ref, computed, h, type Ref } from "vue";
import { useRouter } from "vue-router";
import { DateSelector, LemmonSwitcher, useLocalization } from "@lemmonhq/uikit";
import {
  type FloorPlan,
  type TableStatus,
  type Reservation,
  type ReservationRequest,
} from "@lemmonhq/types";
import { AddFloorPlanModal } from "../AddFloorPlanModal";
import { useAuth } from "../../../providers";
import {
  isConfirmedStatus,
  formatTimeHHmm,
} from "../../../utils/reservationStatus";
import { type FloorPlanViewMode, type DropModeState } from "../tables-types";
import { TablesViewSwitcher } from "./TablesViewSwitcher";

export const FloorPlanHeader = defineComponent<{
  currentView: Ref<string>;
  tables: Ref<TableStatus[]>;
  floorPlans: Ref<FloorPlan[]>;
  activeFloorPlan: Ref<string | undefined>;
  isDirty: Ref<boolean>;
  onSave: () => void;
  onReset: () => void;
  onFloorPlanSelect: (id: string) => void;
  onFloorPlanCreated: () => void;
  isLoading: Ref<boolean>;
  fetchTables: Function;
  floorPlanViewMode: Ref<FloorPlanViewMode>;
  dropModeState: Ref<DropModeState>;
  onEnterDropMode: (reservation: Reservation) => void;
  onExitDropMode: () => void;
  onConfirmDropMode: (action?: "seat" | "assign") => void;
  searchQuery?: Ref<string>;
  filter?: Ref<string>;
  reservations?: Ref<Reservation[]>;
  confirmedCount: number | null;
  pendingCount: number | null;
  seatedCount: number | null;
  selectedDate: Ref<Date>;
  onDateChange: (date: Date) => void;
  isSelectedToday: Ref<boolean>;
  onEnterReservationMode: () => void;
  onExitReservationMode: () => void;
  onOpenReservationList: (tab: "confirmed" | "pending" | "seated") => void;
  onShowReservationDetail: (reservation: Reservation) => void;
  onShowRequestDetail: (request: ReservationRequest) => void;
}>({
  name: "FloorPlanHeader",
  props: [
    "currentView",
    "tables",
    "floorPlans",
    "activeFloorPlan",
    "isDirty",
    "onSave",
    "onReset",
    "onFloorPlanSelect",
    "onFloorPlanCreated",
    "isLoading",
    "fetchTables",
    "floorPlanViewMode",
    "dropModeState",
    "onEnterDropMode",
    "onExitDropMode",
    "onConfirmDropMode",
    "searchQuery",
    "filter",
    "reservations",
    "selectedDate",
    "onDateChange",
    "isSelectedToday",
    "onEnterReservationMode",
    "onExitReservationMode",
    "onOpenReservationList",
    "onShowReservationDetail",
    "onShowRequestDetail",
    "confirmedCount",
    "pendingCount",
    "seatedCount",
  ],
  setup(props) {
    const showAddFloorPlan = ref(false);
    const showEditFloorPlan = ref(false);
    const isSaving = ref(false);
    const auth = useAuth();
    const posMode = auth.posMode;
    const router = useRouter();
    const { t } = useLocalization();
    const restaurantTz = computed(
      () => auth.user.value?.restaurant?.timezone || "UTC",
    );

    const currentFloorPlan = computed(() =>
      props.floorPlans.value.find(
        (fp) => fp.id === props.activeFloorPlan.value,
      ),
    );

    const isEditable = computed(
      () => auth.user.value?.role === "manager" && !posMode.value,
    );

    const sortedFloorPlans = props.floorPlans;

    const roomTables = computed(() => {
      if (props.floorPlanViewMode.value === "all") return props.tables.value;
      const activeFp = props.floorPlans.value.find(
        (fp) => fp.id === props.activeFloorPlan.value,
      );
      if (!activeFp) return props.tables.value;
      const ids = new Set(
        activeFp.plan.filter((p) => p.tableId).map((p) => Number(p.tableId)),
      );
      return props.tables.value.filter((t) => ids.has(Number(t.table.id)));
    });
    const busyCount = computed(
      () => roomTables.value.filter((t) => t.table.is_busy).length,
    );
    const freeCount = computed(() => roomTables.value.length - busyCount.value);
    const totalRevenue = computed(() =>
      roomTables.value.reduce((sum, t) => sum + t.total_amount, 0),
    );
    const reservedCount = computed(() => {
      if (!props.reservations?.value) return 0;
      const roomIds = new Set(roomTables.value.map((t) => Number(t.table.id)));
      return props.reservations.value.filter(
        (r) =>
          isConfirmedStatus(r.status) &&
          r.table_assignments?.some((a) => roomIds.has(Number(a.table_id))),
      ).length;
    });

    const confirmedReservationCount = computed(() => props.confirmedCount);
    const pendingReservationCount = computed(() => props.pendingCount);
    const seatedReservationCount = computed(() => props.seatedCount);

    const btnBase =
      "btn btn-square !h-[30px] !min-h-[30px] !w-[30px] sm:!h-[36px] sm:!min-h-[36px] sm:!w-[36px]";

    const renderReservationCount = (
      count: number | null,
      badgeClass: string,
    ) => {
      if (count === null) {
        return (
          <span
            class={`inline-flex h-[18px] w-[18px] sm:h-[20px] items-center justify-center rounded-md ${badgeClass} animate-pulse motion-reduce:animate-none`}
          />
        );
      }

      return (
        <span
          class={`min-w-[18px] text-center text-[10px] font-semibold rounded-md px-1 py-0.5 ${badgeClass}`}
        >
          {count}
        </span>
      );
    };

    const renderActionButtons = () => {
      const isDropMode = props.dropModeState.value.active;
      const reservation = props.dropModeState.value.reservation;
      const request = props.dropModeState.value.request;

      if (isDropMode && (reservation || request)) {
        const isReq = !!request;
        const count = props.dropModeState.value.selectedTableIds.length;
        const isAssign = isReq || props.dropModeState.value.intent === "assign";
        const canSeat = !isReq && props.isSelectedToday.value;
        const partyName =
          (reservation?.party_name ?? request?.party_name) ||
          t("reservations.general.guest_label");
        const partySize = reservation?.party_size ?? request?.party_size ?? 0;
        const resWhenIso = reservation?.time ?? request!.requested_time;
        const resWhen = new Date(resWhenIso);
        const resDateLabel = resWhen.toLocaleDateString(undefined, {
          weekday: "short",
          day: "numeric",
          month: "short",
          timeZone: restaurantTz.value,
        });
        const resTimeLabel = formatTimeHHmm(resWhenIso, restaurantTz.value);
        return (
          <div class="flex items-center gap-3 bg-primary/10 px-4 py-1.5 rounded-lg border border-primary/30">
            <span class="material-symbols-outlined text-primary text-lg animate-pulse">
              touch_app
            </span>
            <div class="hidden sm:block">
              <div class="font-semibold text-accent text-sm">
                {isAssign
                  ? t("tables.floor_plan.select_table_to_assign")
                  : t("tables.floor_plan.select_table_to_seat")}
              </div>
              <div class="text-xs text-base-content/70 flex items-center gap-1">
                <span class="font-medium text-base-content/85">
                  {partyName}
                </span>
                {" · "}
                <span class="inline-flex items-center gap-0.5 font-medium text-primary/90">
                  <span
                    class="material-symbols-outlined"
                    style="font-size: 13px;"
                  >
                    event
                  </span>
                  {resDateLabel} {resTimeLabel}
                </span>
                {" · "}
                <span>
                  {count} {count === 1 ? "table" : "tables"}
                </span>
                {" · "}
                <span>
                  {partySize} {t("reservations.general.guests")}
                </span>
              </div>
            </div>
            <button
              onClick={() =>
                isReq
                  ? props.onShowRequestDetail(request!)
                  : props.onShowReservationDetail(reservation!)
              }
              class="btn btn-ghost btn-sm !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] gap-1 text-xs"
              title={t("reservations.table_popup.show_details")}
            >
              <span class="material-symbols-outlined text-sm">info</span>
              <span class="hidden ">
                {t("reservations.table_popup.show_details")}
              </span>
            </button>
            <button
              onClick={props.onExitDropMode}
              class="btn btn-ghost btn-sm !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] gap-1 text-xs"
            >
              <span class="material-symbols-outlined text-sm">close</span>
              <span class="hidden ">{t("tables.floor_plan.cancel")}</span>
            </button>
            {isReq ? (
              <button
                onClick={() => props.onConfirmDropMode()}
                class="btn btn-primary btn-sm !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] gap-1 text-xs"
                disabled={count === 0}
              >
                <span class="material-symbols-outlined text-sm">
                  table_restaurant
                </span>
                <span class="hidden sm:inline">
                  {t("reservations.table_popup.assign_confirm")}
                </span>
                {count > 0 ? ` (${count})` : ""}
              </button>
            ) : (
              <>
                <button
                  onClick={() => props.onConfirmDropMode("assign")}
                  class={`btn btn-sm !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] gap-1 text-xs ${
                    canSeat
                      ? "btn-outline border-base-content/25"
                      : "btn-primary"
                  }`}
                  disabled={count === 0}
                >
                  <span class="material-symbols-outlined text-sm">
                    table_restaurant
                  </span>
                  <span class="hidden sm:inline">
                    {t("reservations.table_popup.assign_tables")}
                  </span>
                  {count > 0 ? ` (${count})` : ""}
                </button>
                {canSeat && (
                  <button
                    onClick={() => props.onConfirmDropMode("seat")}
                    class="btn btn-primary btn-sm !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] gap-1 text-xs"
                    disabled={count === 0}
                  >
                    <span class="material-symbols-outlined text-sm">
                      restaurant
                    </span>
                    <span class="hidden sm:inline">
                      {t("reservations.table_popup.seat")}
                    </span>
                    {count > 0 ? ` (${count})` : ""}
                  </button>
                )}
              </>
            )}
          </div>
        );
      }

      return (
        <>
          {props.currentView.value === "floor_plan" && (
            <LemmonSwitcher
              value={isDropMode ? "reservations" : "orders"}
              onChange={(val) => {
                if (val === "orders") {
                  props.onExitReservationMode();
                  return;
                }
                props.onEnterReservationMode();
              }}
              iconOnlyOnMobile
              className="flex-shrink-0 !h-[30px] sm:!h-[36px]"
              buttonClassName="gap-1 px-2 text-[10px] font-medium tracking-wide"
              iconClassName="text-[14px]"
              indicatorClassName="top-[2px] bottom-[2px]"
              options={[
                {
                  value: "orders",
                  icon: "receipt_long",
                  label: "Orders",
                  mobileLabel: "Orders",
                },
                {
                  value: "reservations",
                  icon: "event_available",
                  label: "Res.",
                  mobileLabel: "Reservations",
                },
              ]}
            />
          )}
          {isEditable.value ? (
            <button
              onClick={() => (showEditFloorPlan.value = true)}
              class={`${btnBase} btn-ghost`}
              title={t("tables.floor_plan.edit_floor_plan")}
            >
              <span class="material-symbols-outlined text-lg">settings</span>
            </button>
          ) : null}
          {props.isDirty.value && (
            <div class="flex items-center gap-1.5 ml-1 pl-2 border-l border-base-content/10">
              <button
                onClick={props.onReset}
                class="btn btn-ghost btn-sm !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] gap-1 text-xs"
              >
                <span class="material-symbols-outlined text-sm">undo</span>
                <span class="hidden sm:inline">
                  {t("tables.floor_plan.reset")}
                </span>
              </button>
              <button
                onClick={async () => {
                  isSaving.value = true;
                  try {
                    await props.onSave();
                  } finally {
                    isSaving.value = false;
                  }
                }}
                class="btn btn-primary btn-sm !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] gap-1 text-xs"
                disabled={isSaving.value}
              >
                {isSaving.value ? (
                  <span class="loading loading-spinner loading-xs"></span>
                ) : (
                  <>
                    <span class="material-symbols-outlined text-sm">save</span>
                    <span class="hidden sm:inline">
                      {t("tables.floor_plan.save")}
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </>
      );
    };

    return () => {
      const isAllView = props.floorPlanViewMode.value === "all";
      const activeId = props.activeFloorPlan.value;

      const handleTabClick = (val: string) => {
        if (isAllView) {
          const el = document.getElementById(`floor-plan-${val}`);
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          props.floorPlanViewMode.value = "selected";
          props.onFloorPlanSelect(val);
        }
      };

      const isList = props.currentView.value === "list";
      const hasSearch = props.searchQuery && props.filter;

      return (
        <div class="w-full bg-base-100">
          {/* Mobile */}
          <div class="sm:hidden">
            {hasSearch && (
              <div class="flex items-center gap-2 px-3 py-2 border-b ">
                <div class="relative flex-1">
                  <span
                    class="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40"
                    style="font-size: 14px;"
                  >
                    search
                  </span>
                  <input
                    type="text"
                    v-model={props.searchQuery!.value}
                    placeholder="Search"
                    class="input input-sm bg-base-200 pl-8 pr-3 w-full text-xs"
                    style="height: 30px; min-height: 30px;"
                  />
                </div>
                <select
                  class="select select-sm bg-base-200 border-base-content/10 text-xs"
                  style="height: 30px; min-height: 30px; width: 80px;"
                  v-model={props.filter!.value}
                >
                  <option value="free">{t("general.words.free")}</option>
                  <option value="busy">{t("general.words.busy")}</option>
                </select>
              </div>
            )}
            <div class="border-b border-base-300 overflow-x-auto">
              <div class="flex items-center px-2 min-w-max">
                <button
                  type="button"
                  class={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all ml-2 flex-shrink-0 ${
                    isAllView
                      ? "bg-accent/15 text-accent border border-accent/30"
                      : "bg-base-200 text-base-content/50 border border-transparent hover:text-base-content/70"
                  }`}
                  onClick={() => {
                    props.floorPlanViewMode.value = isAllView
                      ? "selected"
                      : "all";
                  }}
                >
                  <span class="material-symbols-outlined text-sm">
                    select_all
                  </span>
                  All
                </button>
                {isEditable.value ? (
                  <button
                    type="button"
                    class="px-2 py-2.5 text-base-content/40 hover:text-base-content/70 transition-colors border-b-2 border-transparent"
                    onClick={() => (showAddFloorPlan.value = true)}
                    title={t("tables.floor_plan.add_floor_plan")}
                  >
                    <span class="material-symbols-outlined text-base">add</span>
                  </button>
                ) : null}
                <div class="flex flex-1 min-w-0">
                  {sortedFloorPlans.value.map((el) => {
                    const isActive = !isAllView && activeId === el.id;
                    return (
                      <button
                        key={el.id}
                        type="button"
                        class={`px-2 py-2.5 text-xs font-medium transition-all flex items-center justify-center gap-1 whitespace-nowrap border-b-2 ${
                          isActive
                            ? "text-accent border-accent font-semibold"
                            : "text-base-content/60 border-transparent hover:text-base-content/80"
                        }`}
                        onClick={() => handleTabClick(el.id)}
                      >
                        {el.color ? (
                          <span
                            class="w-2 h-2 rounded-full inline-block flex-shrink-0"
                            style={{
                              background: el.color,
                              border: "1px solid rgba(255,255,255,0.15)",
                            }}
                          />
                        ) : null}
                        {el.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop/Tablet */}
          <div class="hidden sm:block">
            <div class="px-3 xl:px-6 py-2.5 border-b bg-base-200 ">
              <div class="flex items-center gap-2">
                {renderActionButtons()}
                {props.dropModeState.value.active &&
                  !props.dropModeState.value.reservation &&
                  !props.dropModeState.value.request && (
                    <>
                      <div class="w-px h-5 bg-base-content/15" />
                      <DateSelector
                        selectedDate={props.selectedDate.value}
                        onDateChange={props.onDateChange}
                        compact
                        controlHeightClass="!h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px]"
                      />
                      <button
                        onClick={() =>
                          router.push("/app/manager/reservations/schedule")
                        }
                        class="btn btn-sm gap-1.5 !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] text-xs bg-base-200 border-base-300 hover:bg-base-200"
                        title={t("reservations.nav.schedule")}
                      >
                        <span class="material-symbols-outlined text-base">
                          calendar_month
                        </span>
                        <span class="hidden lg:inline">
                          {t("reservations.nav.schedule")}
                        </span>
                      </button>
                    </>
                  )}

                {isList && hasSearch && (
                  <div class="flex items-center gap-1.5 flex-1 min-w-0 mx-0">
                    <div class="relative flex-1 max-w-[200px]">
                      <span
                        class="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40"
                        style="font-size: 14px;"
                      >
                        search
                      </span>
                      <input
                        type="text"
                        v-model={props.searchQuery!.value}
                        placeholder="Search"
                        class="input input-sm bg-base-200 pl-8 pr-3 w-full text-xs"
                        style="height: 36px; min-height: 36px;"
                      />
                    </div>
                    <select
                      class="select select-sm bg-base-200 border-base-content/10 text-xs"
                      style="height: 36px; min-height: 36px; width: 80px;"
                      v-model={props.filter!.value}
                    >
                      <option value="free">{t("general.words.free")}</option>
                      <option value="busy">{t("general.words.busy")}</option>
                    </select>
                  </div>
                )}

                <div class="flex items-center gap-3 flex-shrink-0 ml-auto">
                  {props.dropModeState.value.active &&
                    !props.dropModeState.value.reservation &&
                    !props.dropModeState.value.request && (
                      <div class="flex items-center gap-1.5">
                        {[
                          {
                            tab: "confirmed" as const,
                            icon: "event_available",
                            labelKey: "reservations.table_popup.confirmed_tab",
                            count: confirmedReservationCount.value,
                            iconClass: "text-success",
                            badgeClass: "bg-success/15 text-success",
                          },
                          {
                            tab: "pending" as const,
                            icon: "table_restaurant",
                            labelKey: "reservations.table_popup.pending_tab",
                            count: pendingReservationCount.value,
                            iconClass: "text-warning",
                            badgeClass: "bg-warning/15 text-warning",
                          },
                          {
                            tab: "seated" as const,
                            icon: "restaurant",
                            labelKey: "reservations.table_popup.seated_tab",
                            count: seatedReservationCount.value,
                            iconClass: "text-info",
                            badgeClass: "bg-info/15 text-info",
                          },
                        ].map((chip) => (
                          <button
                            key={chip.tab}
                            onClick={() =>
                              props.onOpenReservationList(chip.tab)
                            }
                            class="btn btn-sm gap-1.5 !h-[30px] !min-h-[30px] sm:!h-[36px] sm:!min-h-[36px] text-xs bg-base-200 border-base-300 hover:bg-base-200"
                            title={t(chip.labelKey)}
                          >
                            <span
                              class={`material-symbols-outlined text-base ${chip.iconClass}`}
                            >
                              {chip.icon}
                            </span>
                            <span class="hidden lg:inline">
                              {t(chip.labelKey)}
                            </span>
                            {renderReservationCount(
                              chip.count,
                              chip.badgeClass,
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  <div
                    class={`${
                      props.dropModeState.value.active
                        ? "hidden"
                        : "hidden lg:flex"
                    } items-center gap-3 text-[11px] text-base-content/50`}
                  >
                    <span class="flex items-center gap-1">
                      <span class="w-2 h-2 rounded-full bg-gray-400 inline-block" />
                      {freeCount.value} free
                    </span>
                    <span class="flex items-center gap-1">
                      <span class="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                      {busyCount.value} busy
                    </span>
                    {reservedCount.value > 0 && (
                      <span class="flex items-center gap-1">
                        <span class="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                        {reservedCount.value} reserved
                      </span>
                    )}
                    <span class="flex items-center gap-1">
                      <span
                        class="material-symbols-outlined"
                        style="font-size: 12px;"
                      >
                        payments
                      </span>
                      {auth.user.value?.restaurant.currency || "CHF"}{" "}
                      {totalRevenue.value.toFixed(0)}
                    </span>
                  </div>

                  {props.isLoading.value ? (
                    <div class="loading loading-spinner loading-xs" />
                  ) : null}

                  <TablesViewSwitcher
                    currentView={props.currentView}
                    isReservationMode={props.dropModeState.value.active}
                  />
                </div>
              </div>
            </div>

            {/* Room Tabs */}
            <div class="border-b bg-base-200/50">
              <div class="flex items-center px-3 xl:px-6 min-h-12">
                <div class="flex flex-1 min-w-0">
                  {sortedFloorPlans.value.map((el) => {
                    const isActive = !isAllView && activeId === el.id;
                    return (
                      <button
                        key={el.id}
                        type="button"
                        class={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 whitespace-nowrap border-b-2 ${
                          isActive
                            ? "text-accent border-accent font-semibold"
                            : "text-base-content/60 border-transparent hover:text-base-content/80"
                        }`}
                        onClick={() => handleTabClick(el.id)}
                      >
                        {el.color ? (
                          <span
                            class="w-2 h-2 rounded-full inline-block flex-shrink-0"
                            style={{
                              background: el.color,
                              border: "1px solid rgba(255,255,255,0.15)",
                            }}
                          />
                        ) : null}
                        {el.name}
                      </button>
                    );
                  })}

                  {isEditable.value ? (
                    <button
                      type="button"
                      class="px-2 py-3 text-base-content/40 hover:text-base-content/70 transition-colors border-b-2 border-transparent"
                      onClick={() => (showAddFloorPlan.value = true)}
                      title={t("tables.floor_plan.add_floor_plan")}
                    >
                      <span class="material-symbols-outlined text-lg">add</span>
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  class={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ml-2 flex-shrink-0 ${
                    isAllView
                      ? "bg-accent/15 text-accent border border-accent/30"
                      : "bg-base-200 text-base-content/50 border border-transparent hover:text-base-content/70"
                  }`}
                  onClick={() => {
                    props.floorPlanViewMode.value = isAllView
                      ? "selected"
                      : "all";
                  }}
                >
                  <span class="material-symbols-outlined text-sm">
                    select_all
                  </span>
                  All
                </button>
              </div>
            </div>
          </div>

          {showAddFloorPlan.value && (
            <AddFloorPlanModal
              onClose={() => (showAddFloorPlan.value = false)}
              onSave={() => {
                props.onFloorPlanCreated();
                showAddFloorPlan.value = false;
              }}
              onDelete={() => {
                props.fetchTables();
              }}
            />
          )}

          {showEditFloorPlan.value && currentFloorPlan.value && (
            <AddFloorPlanModal
              editingFloorPlan={currentFloorPlan.value}
              onClose={() => (showEditFloorPlan.value = false)}
              onSave={() => {
                props.onFloorPlanCreated();
                showEditFloorPlan.value = false;
              }}
              onDelete={() => {
                props.fetchTables();
              }}
            />
          )}
        </div>
      );
    };
  },
});
